import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import {
  useContributionRolesDDL,
  useInfiniteLoader,
  useResource,
} from 'app/utils/hooks';
import { Credit } from 'types/api.types';
import {
  getCreditsList,
  getCreditsListNextLink,
} from 'app/store/Contributions/selectors';
import { fetchCreditsListEffect } from 'app/store/Contributions/effects';
import { getContributionClassNames } from 'app/store/SongList/selectors';
import { fetchContributionClassesRolesDDLEffect } from 'app/store/SongList/effects';
import { ReactComponent as CreditsIcon } from 'resources/images/credits.svg';
import { useContextMenu } from 'app/components/ChakraTableList';
import { ChakraEmptyList } from 'app/components/Core';
import { CreditListDialog } from 'app/components/Song/Credits/CreditsListDialog';
import {
  ContributionClassRolesDDL,
  ContributionsListItem,
} from 'types/contribution';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { getColumns } from './utils';
import { CreditRow } from './CreditRow';

export interface CreditsListProps {
  credits: Credit[];
  loading: boolean;
  contributionClassNames?: ContributionClassRolesDDL;
  contributionRoles?: ContributionClassRolesDDL;
}

export const CreditsListComponent: React.FC<CreditsListProps> = ({
  credits,
  loading,
  contributionClassNames = {},
  contributionRoles,
}) => {
  const [openCredit, setOpenCredit] = useState<Credit>();

  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    dataLength: credits.length,
    nextLinkSelector: getCreditsListNextLink,
    fetchNext: (link) => fetchCreditsListEffect({ next: link }),
  });

  const onRowClickCallback = useCallback((credit: Credit) => {
    setOpenCredit(credit);
  }, []);

  const tableKey = 'profile-credits';
  const ref = useRef<HTMLDivElement>(null);
  const contextMenuProps = useContextMenu();
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns({ loading, totalWidth }), [
    loading,
    totalWidth,
  ]);

  const rowRenderer = useCallback(
    (item: Row<Credit>) => {
      const credit = item.original;
      return (
        <CreditRow
          key={credit.guid}
          credit={credit}
          contributionClassNames={contributionClassNames}
          contributionRoles={contributionRoles}
          onRowClick={onRowClickCallback}
        />
      );
    },
    [contributionClassNames, contributionRoles, onRowClickCallback]
  );

  if (!loading && credits.length === 0) {
    return (
      <ChakraEmptyList
        icon={<CreditsIcon role="img" />}
        title="Credits"
        primaryText="Always get the credit you deserve. Add your credit to a song using the 'Studio Check-in' QR scanner from the mobile app then scan the QR code in the desktop or web application."
        secondaryText="Make Music, Get Credit"
        secondaryTextSx={{
          pt: 4,
        }}
      />
    );
  }

  return (
    <Box pt="6">
      <StickyTable<ContributionsListItem>
        tableKey={tableKey}
        ref={ref}
        data={credits}
        columns={columns}
        infiniteScrollProps={infiniteScrollProps}
        loadingMore={loadingMore}
        rowRenderer={rowRenderer}
        contextMenuProps={contextMenuProps}
        loading={loading}
      />
      {openCredit && (
        <CreditListDialog
          onClose={() => setOpenCredit(undefined)}
          isOpen={Boolean(openCredit)}
          credit={openCredit}
          title={openCredit.title}
          contributionRoles={contributionRoles}
        />
      )}
    </Box>
  );
};

export const CreditsList = () => {
  const [credits, creditsLoading] = useResource(
    fetchCreditsListEffect,
    getCreditsList,
    { useCache: false }
  );

  const [contributionClassNames] = useResource(
    fetchContributionClassesRolesDDLEffect,
    getContributionClassNames
  );

  const contributionRolesDDL = useContributionRolesDDL();

  return (
    <CreditsListComponent
      credits={credits || []}
      loading={creditsLoading}
      contributionClassNames={contributionClassNames}
      contributionRoles={contributionRolesDDL}
    />
  );
};
